import moment from 'moment-timezone';
import { DisjunctiveAvailabilityIterator, ConjunctiveTimeWindowsIterator, AvailabilityIterator, } from 'availability';
import { isSupportsFutureOrder } from './restaurantLogic';
import { getDateOptionsV2, getTimeOptions } from './dateTimeLogic';
function dtlv2_getSupportedDispatchTypesInner(_a) {
    var restaurant = _a.restaurant, when = _a.when;
    var cal = moment(when).tz(restaurant.timezone);
    var result = {};
    ['takeout', 'dine-in', 'delivery'].forEach(function (dt) {
        var _a = getCombinedAvailabilityIteratorByDispatchType(cal, restaurant, dt), asapIterator = _a.asapIterator, futureOrdersIterator = _a.futureOrdersIterator;
        var status = asapIterator.next().status;
        var _b = futureOrdersIterator.next(), futureStatus = _b.status, futureUntil = _b.until;
        result[dt] = {
            isAvailable: status === 'available',
            willBeAvailable: isSupportsFutureOrder(restaurant) &&
                (futureStatus === 'available' || Boolean(futureUntil)),
        };
    });
    return result;
}
export function dtlv2_getSupportedDispatchTypesSet(args) {
    return toSet(dtlv2_getSupportedDispatchTypes(args));
}
function getIterators(cal, restaurant, dispatchType) {
    return [
        getAvailabilityIteratorByDispatchType(cal, restaurant, dispatchType),
        new AvailabilityIterator({
            availability: restaurant.openTimes,
            cal: cal,
        }),
        new AvailabilityIterator({
            availability: restaurant.orders.availability,
            cal: cal,
        }),
    ];
}
function getCombinedAvailabilityIteratorByDispatchType(cal, restaurant, dispatchType) {
    var iterators = getIterators(cal, restaurant, dispatchType);
    var iterators2 = getIterators(cal, restaurant, dispatchType);
    var asapIterator = new ConjunctiveTimeWindowsIterator({
        cal: cal,
        iterators: iterators,
    });
    var futureOrdersIterator = new ConjunctiveTimeWindowsIterator({
        cal: cal,
        iterators: iterators2.concat(getFutureOrdersAvailability(cal, restaurant)),
    });
    return { asapIterator: asapIterator, futureOrdersIterator: futureOrdersIterator };
}
function getAvailabilityIteratorByDispatchType(cal, restaurant, dispatchType) {
    var _a;
    if (dispatchType === 'takeout') {
        var takeout = restaurant.deliveryInfos.find(function (di) { return di.type === 'takeout'; });
        if (!takeout || takeout.inactive) {
            return getUnavailableIteratorLikeObject();
        }
        else {
            return new AvailabilityIterator({
                availability: takeout.availability,
                cal: cal,
            });
        }
    }
    else if (dispatchType === 'dine-in') {
        var takeout = restaurant.deliveryInfos.find(function (di) { return di.type === 'takeout'; });
        if (!takeout ||
            takeout.inactive ||
            !((_a = takeout.contactlessDineInInfo) === null || _a === void 0 ? void 0 : _a.enabled)) {
            return getUnavailableIteratorLikeObject();
        }
        else {
            return new AvailabilityIterator({
                availability: takeout.availability,
                cal: cal,
            });
        }
    }
    else if (dispatchType === 'delivery') {
        var deliveryInfos = restaurant.deliveryInfos.filter(function (di) { return di.type === 'delivery'; });
        if (deliveryInfos.length === 0) {
            return getUnavailableIteratorLikeObject();
        }
        else {
            return new DisjunctiveAvailabilityIterator({
                availabilities: deliveryInfos.map(function (di) { return di.availability; }),
                cal: cal,
            });
        }
    }
}
function getUnavailableIteratorLikeObject() {
    return {
        hasNext: function () { return false; },
        next: function () { return ({ status: 'unavailable', until: null }); },
    };
}
export function dtlv2_getDefaultDispatchType(_a) {
    var restaurants = _a.restaurants, when = _a.when;
    var set = dtlv2_getSupportedDispatchTypesSet({
        restaurants: restaurants,
        when: when,
    });
    if (set.has('delivery')) {
        return 'delivery';
    }
    else if (set.has('takeout')) {
        return 'takeout';
    }
    else if (set.has('dine-in')) {
        return 'dine-in';
    }
    else {
        throw new Error('No supported dispatch types');
    }
}
function toSet(supportedDispatchTypes) {
    var set = new Set();
    Object.keys(supportedDispatchTypes).forEach(function (dt) {
        var dta = supportedDispatchTypes[dt];
        if ((dta === null || dta === void 0 ? void 0 : dta.isAvailable) || (dta === null || dta === void 0 ? void 0 : dta.willBeAvailable)) {
            set.add(dt);
        }
    });
    return set;
}
function toAvailabilityDate(cal) {
    return {
        year: cal.get('year'),
        month: cal.get('month') + 1,
        day: cal.get('date'),
        hour: cal.get('hour'),
        minute: cal.get('minute'),
    };
}
function getFutureOrdersAvailability(cal, restaurant) {
    var now = moment().tz(restaurant.timezone);
    var nowPlusMinTime = moment()
        .tz(restaurant.timezone)
        .add(restaurant.orders.future.delayMins.min, 'minutes');
    var nowPlusMaxTime = moment()
        .tz(restaurant.timezone)
        .add(restaurant.orders.future.delayMins.max, 'minutes');
    var availability = {
        exceptions: [
            {
                available: false,
                start: toAvailabilityDate(now),
                end: toAvailabilityDate(nowPlusMinTime),
            },
            {
                available: false,
                start: toAvailabilityDate(nowPlusMaxTime),
            },
        ],
    };
    return new AvailabilityIterator({ availability: availability, cal: cal });
}
function isAsapAvailable(restaurant, dispatchType) {
    var cal = moment().tz(restaurant.timezone);
    var asapIterator = getCombinedAvailabilityIteratorByDispatchType(cal, restaurant, dispatchType).asapIterator;
    var asapStatus = asapIterator.next().status;
    return asapStatus === 'available';
}
export function dtlv2_getEarliestDispatchTime(restaurant, dispatchType) {
    if (isAsapAvailable(restaurant, dispatchType)) {
        return { timingOption: 'asap' };
    }
    var cal = moment().tz(restaurant.timezone);
    var availabilityIteratorFactory = function (c) {
        return getCombinedAvailabilityIteratorByDispatchType(c, restaurant, dispatchType).futureOrdersIterator;
    };
    var dateOptions = getDateOptionsV2(availabilityIteratorFactory, cal, restaurant.orders.future.delayMins.max / 1440, restaurant.timezone, 0);
    var timeOptions = getTimeOptions({
        day: dateOptions[0].timestamp,
        locale: restaurant.locale,
        timezone: restaurant.timezone,
        availabilityIteratorFactory: availabilityIteratorFactory,
        breakOnFirst: true,
    });
    return { timingOption: 'future', timestamp: timeOptions[0].timestamp };
}
export function dtlv2_getSupportedDispatchTypes(_a) {
    var restaurants = _a.restaurants, when = _a.when;
    return restaurants
        .map(function (restaurant) {
        return dtlv2_getSupportedDispatchTypesInner({ restaurant: restaurant, when: when });
    })
        .reduce(function (previousValue, currentValue) {
        var result = {};
        ['takeout', 'dine-in', 'delivery'].forEach(function (dt) {
            var _a, _b, _c, _d;
            var dispatchType = dt;
            result[dispatchType] = {
                isAvailable: Boolean(((_a = previousValue[dispatchType]) === null || _a === void 0 ? void 0 : _a.isAvailable) || ((_b = currentValue[dispatchType]) === null || _b === void 0 ? void 0 : _b.isAvailable)),
                willBeAvailable: Boolean(((_c = previousValue[dispatchType]) === null || _c === void 0 ? void 0 : _c.willBeAvailable) || ((_d = currentValue[dispatchType]) === null || _d === void 0 ? void 0 : _d.willBeAvailable)),
            };
        });
        return result;
    }, {});
}
//# sourceMappingURL=dispatchTypeLogicV2.js.map